import { useState } from 'react';
import { useOfficeSpaceStore } from '../../store/officeSpaceStore';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

// Create + Leave office-space dialogs, controlled from outside. Extracted so they
// can be rendered OUTSIDE a dropdown menu (which unmounts its content on close).
export default function OfficeSpaceDialogs({
    createOpen, setCreateOpen,
    leaveOpen, setLeaveOpen,
    officeToLeave, setOfficeToLeave,
}) {
    const { t } = useTranslation();
    const { createOfficeSpace, leaveOfficeSpace, isLoading } = useOfficeSpaceStore();
    const [newOfficeName, setNewOfficeName] = useState('');
    const [creating, setCreating] = useState(false);

    const handleCreate = async () => {
        if (!newOfficeName.trim()) {
            notifications.show({ title: t('common.error'), message: t('officeSpace.pleaseEnterOfficeSpaceName'), color: 'red' });
            return;
        }
        setCreating(true);
        try {
            await createOfficeSpace({ name: newOfficeName.trim() });
            notifications.show({ title: t('common.success'), message: t('officeSpace.officeSpaceCreated'), color: 'green' });
            setCreateOpen(false);
            setNewOfficeName('');
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err.message || t('officeSpace.failedToCreateOfficeSpace'), color: 'red' });
        } finally {
            setCreating(false);
        }
    };

    const handleLeave = async () => {
        if (!officeToLeave) return;
        try {
            await leaveOfficeSpace(officeToLeave.id);
            notifications.show({ title: t('common.success'), message: t('officeSpace.leftOfficeSpace', { name: officeToLeave.name }), color: 'green' });
            setLeaveOpen(false);
            setOfficeToLeave(null);
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err.message || t('officeSpace.failedToLeaveOfficeSpace'), color: 'red' });
        }
    };

    return (
        <>
            {/* Create */}
            <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) setNewOfficeName(''); }}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>{t('officeSpace.createNewOfficeSpace')}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                                {t('officeSpace.workspaceName')}
                            </label>
                            <input
                                type="text"
                                value={newOfficeName}
                                onChange={(e) => setNewOfficeName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                                placeholder={t('officeSpace.enterOfficeSpaceName')}
                                className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600
                                    bg-white dark:bg-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setCreateOpen(false)}
                                className="px-3 py-1.5 text-sm rounded-md border border-zinc-300 dark:border-zinc-600 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors">
                                {t('common.cancel')}
                            </button>
                            <button onClick={handleCreate} disabled={creating}
                                className="px-3 py-1.5 text-sm rounded-md bg-orange-500 hover:bg-orange-600 text-white font-medium transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                                {creating && <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                                {t('officeSpace.create')}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Leave */}
            <Dialog open={leaveOpen} onOpenChange={(v) => { setLeaveOpen(v); if (!v) setOfficeToLeave(null); }}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>{t('officeSpace.confirmLeaveOfficeSpace')}</DialogTitle></DialogHeader>
                    <p className="text-sm text-zinc-700 dark:text-zinc-300">
                        {t('officeSpace.leaveOfficeSpaceWarning', { name: officeToLeave?.name })}
                    </p>
                    <div className="flex justify-end gap-2 mt-2">
                        <button onClick={() => setLeaveOpen(false)}
                            className="px-3 py-1.5 text-sm rounded-md border border-zinc-300 dark:border-zinc-600 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors">
                            {t('common.cancel')}
                        </button>
                        <button onClick={handleLeave} disabled={isLoading}
                            className="px-3 py-1.5 text-sm rounded-md bg-red-600 hover:bg-red-700 text-white font-medium transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                            {isLoading && <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                            {t('officeSpace.leave')}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
